import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import HeaderLayout from "../common/HeaderLayout";

const API_URL = "https://api.rangpursadhima.cloud/api/patotsav";

export default function Patotsav() {
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [summary, setSummary] = useState(null);
  const [kurtaSummary, setKurtaSummary] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddDeptModal, setShowAddDeptModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [addToDept, setAddToDept] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchDepartments();
    fetchSummary();
    fetchKurtaSummary();
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [selectedDept]);

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/departments`);
      setDepartments(res.data);
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const url = selectedDept 
        ? `${API_URL}/members?department_id=${selectedDept}`
        : `${API_URL}/members`;
      const res = await axios.get(url);
      setMembers(res.data);
    } catch (err) {
      console.error("Failed to fetch members:", err);
    }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/summary`);
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  };

  const fetchKurtaSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/kurta-summary`);
      setKurtaSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch kurta summary:", err);
    }
  };

  // Optimistic update for inline toggle buttons (no scroll jump)
  const updateMemberOptimistic = async (id, data) => {
    // Update local state immediately (optimistic update)
    setMembers(prevMembers => 
      prevMembers.map(m => m.id === id ? { ...m, ...data } : m)
    );

    try {
      await axios.put(`${API_URL}/members/${id}`, data);
      // Update summary in background without affecting scroll
      fetchSummary();
    } catch (err) {
      console.error("Failed to update member:", err);
      alert("Failed to update");
      // Revert on error - refetch data
      fetchMembers();
    }
  };

  // Full update for modal edits (will refetch)
  const updateMember = async (id, data) => {
    try {
      await axios.put(`${API_URL}/members/${id}`, data);
      fetchMembers();
      fetchSummary();
      setEditingMember(null);
    } catch (err) {
      console.error("Failed to update member:", err);
      alert("Failed to update");
    }
  };

  const addMember = async (data) => {
    try {
      await axios.post(`${API_URL}/members`, data);
      fetchMembers();
      fetchSummary();
      fetchDepartments();
      setShowAddModal(false);
      setAddToDept(null);
    } catch (err) {
      console.error("Failed to add member:", err);
      alert("Failed to add member");
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    try {
      await axios.delete(`${API_URL}/members/${id}`);
      // Optimistic delete
      setMembers(prevMembers => prevMembers.filter(m => m.id !== id));
      fetchSummary();
      fetchDepartments();
    } catch (err) {
      console.error("Failed to delete member:", err);
      fetchMembers(); // Revert on error
    }
  };

  // Add new department
  const addDepartment = async (data) => {
    try {
      await axios.post(`${API_URL}/departments`, data);
      fetchDepartments();
      setShowAddDeptModal(false);
    } catch (err) {
      console.error("Failed to add department:", err);
      alert("Failed to add department: " + (err.response?.data?.error || err.message));
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const [allPdfLoading, setAllPdfLoading] = useState(false);

  // Download PDF for a single department
  const downloadDepartmentPDF = async (deptId, deptName, deptMembers) => {
    setPdfLoading(true);
    
    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 1000px;
      padding: 20px;
      background: white;
      font-family: 'Noto Sans Gujarati', 'Shruti', Arial, sans-serif;
    `;
    
    container.innerHTML = `
      <div style="margin-bottom: 20px;">
        <h1 style="color: #B40000; font-size: 24px; margin: 0;">[${deptId}] ${deptName}</h1>
        <p style="color: #666; font-size: 14px; margin: 5px 0;">પાટોત્સવ 2026 | કુલ: ${deptMembers.length} સભ્યો</p>
      </div>
      <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
        <thead>
          <tr style="background: #B40000; color: white;">
            <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">#</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">નામ</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">હોદ્દો</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">મોબાઈલ</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">શહેર</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">સાઈઝ</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">ID</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">ID આપેલ</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">કુર્તા</th>
            <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">ઉપલબ્ધ</th>
          </tr>
        </thead>
        <tbody>
          ${deptMembers.map((m, i) => `
            <tr style="background: ${i % 2 === 0 ? '#fff' : '#f9f9f9'};">
              <td style="border: 1px solid #ddd; padding: 8px;">${i + 1}</td>
              <td style="border: 1px solid #ddd; padding: 8px; font-weight: 500;">${m.name || '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">${m.position || '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">${m.mobile || '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px;">${m.city || '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${m.kurta_size || '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${m.id_card || '-'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${m.id_card_given === 'Y' ? 'green' : '#666'};">${m.id_card_given === 'Y' ? '✓' : 'X'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${m.kurta_given === 'Y' ? 'green' : '#666'};">${m.kurta_given === 'Y' ? '✓' : 'X'}</td>
              <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${m.availability === 'Y' ? 'green' : 'red'};">
                ${m.availability === 'Y' ? 'હા' : `ના${m.availability_reason ? ` (${m.availability_reason})` : ''}`}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    
    document.body.appendChild(container);
    
    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 10;
      
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${deptName.replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed');
    } finally {
      document.body.removeChild(container);
      setPdfLoading(false);
    }
  };

  // Download PDF for ALL departments
  const downloadAllDepartmentsPDF = async () => {
    setAllPdfLoading(true);
    
    try {
      // Fetch all members if not already loaded
      const res = await axios.get(`${API_URL}/members`);
      const allMembers = res.data;
      
      // Group by department
      const grouped = allMembers.reduce((acc, member) => {
        const deptId = member.department_id;
        if (!acc[deptId]) {
          acc[deptId] = {
            department_name: member.department_name,
            members: []
          };
        }
        acc[deptId].members.push(member);
        return acc;
      }, {});

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const sortedDeptIds = Object.keys(grouped).sort((a, b) => parseInt(a) - parseInt(b));
      
      for (let i = 0; i < sortedDeptIds.length; i++) {
        const deptId = sortedDeptIds[i];
        const group = grouped[deptId];
        
        if (i > 0) {
          pdf.addPage();
        }

        // Create container for this department
        const container = document.createElement('div');
        container.style.cssText = `
          position: absolute;
          left: -9999px;
          top: 0;
          width: 1000px;
          padding: 20px;
          background: white;
          font-family: 'Noto Sans Gujarati', 'Shruti', Arial, sans-serif;
        `;
        
        container.innerHTML = `
          <div style="margin-bottom: 20px;">
            <h1 style="color: #B40000; font-size: 24px; margin: 0;">[${deptId}] ${group.department_name}</h1>
            <p style="color: #666; font-size: 14px; margin: 5px 0;">પાટોત્સવ 2026 | કુલ: ${group.members.length} સભ્યો</p>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead>
              <tr style="background: #B40000; color: white;">
                <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">#</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">નામ</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">હોદ્દો</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">મોબાઈલ</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: left;">શહેર</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">સાઈઝ</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">ID</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">ID આપેલ</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">કુર્તા</th>
                <th style="border: 1px solid #B40000; padding: 8px; text-align: center;">ઉપલબ્ધ</th>
              </tr>
            </thead>
            <tbody>
              ${group.members.map((m, idx) => `
                <tr style="background: ${idx % 2 === 0 ? '#fff' : '#f9f9f9'};">
                  <td style="border: 1px solid #ddd; padding: 8px;">${idx + 1}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; font-weight: 500;">${m.name || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${m.position || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${m.mobile || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${m.city || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${m.kurta_size || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${m.id_card || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${m.id_card_given === 'Y' ? 'green' : '#666'};">${m.id_card_given === 'Y' ? '✓' : 'X'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${m.kurta_given === 'Y' ? 'green' : '#666'};">${m.kurta_given === 'Y' ? '✓' : 'X'}</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: center; color: ${m.availability === 'Y' ? 'green' : 'red'};">
                    ${m.availability === 'Y' ? 'હા' : `ના${m.availability_reason ? ` (${m.availability_reason})` : ''}`}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
        
        document.body.appendChild(container);
        
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          logging: false
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 20;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        let heightLeft = imgHeight;
        let position = 10;
        
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        
        while (heightLeft > 0) {
          position = heightLeft - imgHeight + 10;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        
        document.body.removeChild(container);
      }
      
      pdf.save('Patotsav_2026_All_Departments.pdf');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      setAllPdfLoading(false);
    }
  };

  const filteredMembers = members.filter(m => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      m.name?.toLowerCase().includes(term) ||
      m.mobile?.includes(term) ||
      m.city?.toLowerCase().includes(term) ||
      m.department_name?.toLowerCase().includes(term)
    );
  });

  // Group by department for display
  const groupedMembers = filteredMembers.reduce((acc, member) => {
    const deptId = member.department_id;
    if (!acc[deptId]) {
      acc[deptId] = {
        department_name: member.department_name,
        members: []
      };
    }
    acc[deptId].members.push(member);
    return acc;
  }, {});

  return (
    <HeaderLayout title="પાટોત્સવ યજ્ઞ - 2026">
    <div className="p-4 max-w-7xl mx-auto">
      {/* Date Info */}
      <div className="mb-6">
        <p className="text-red-600">તારીખ 09/02/2026 શુક્રવાર , 10/02/2026 શનિવાર</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="text-2xl font-bold text-red-700">{summary.total_members}</div>
            <div className="text-sm text-red-600">કુલ સભ્યો</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-700">{summary.available_count}</div>
            <div className="text-sm text-green-600">ઉપલબ્ધ</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-300">
            <div className="text-2xl font-bold text-red-700">{summary.unavailable_count}</div>
            <div className="text-sm text-red-600">અનુપલબ્ધ</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-700">{summary.id_card_given_count}</div>
            <div className="text-sm text-purple-600">ID આપેલ</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-2xl font-bold text-orange-700">{summary.kurta_given_count}</div>
            <div className="text-sm text-orange-600">કુર્તા આપેલ</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="text-2xl font-bold text-red-700">{departments.length}</div>
            <div className="text-sm text-red-600">વિભાગો</div>
          </div>
        </div>
      )}

      {/* Kurta Size Summary */}
      {kurtaSummary.length > 0 && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow border border-red-100">
          <h3 className="font-semibold mb-3 text-red-700">કુર્તા સાઈઝ સારાંશ</h3>
          <div className="flex flex-wrap gap-3">
            {kurtaSummary.map(k => (
              <div key={k.kurta_size} className="px-4 py-2 bg-red-50 rounded-lg border border-red-200">
                <span className="font-bold text-red-700">{k.kurta_size}:</span> <span className="text-red-600">{k.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filter Section */}
      <div className="mb-6 p-4 bg-white rounded-lg shadow border border-red-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <input
            type="text"
            placeholder="નામ, મોબાઈલ, શહેર શોધો..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <select
            value={selectedDept || ""}
            onChange={(e) => setSelectedDept(e.target.value || null)}
            className="w-full px-4 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
          >
            <option value="">બધા વિભાગ ({members.length})</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>
                [{d.id}] {d.name} ({d.member_count})
              </option>
            ))}
          </select>
        </div>
        
        {/* Action Buttons Row */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowAddDeptModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
          >
            <span>➕</span> નવો વિભાગ
          </button>
          <button
            onClick={() => {
              setAddToDept(null);
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2"
          >
            <span>➕</span> નવો સભ્ય
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2"
          >
            <span>📊</span> Export Excel
          </button>
          <button
            onClick={downloadAllDepartmentsPDF}
            disabled={allPdfLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {allPdfLoading ? (
              <>
                <span className="animate-spin">⏳</span> PDF બની રહ્યું છે...
              </>
            ) : (
              <>
                <span>📄</span> બધા વિભાગ PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Members Table */}
      {loading ? (
        <div className="text-center py-8 text-red-500">Loading...</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedMembers)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([deptId, group]) => (
            <div key={deptId} className="bg-white rounded-lg shadow overflow-hidden border border-red-200">
              <div className="bg-red-700 text-white px-4 py-3 flex justify-between items-center">
                <span className="font-semibold">
                  [{deptId}] {group.department_name} ({group.members.length})
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setAddToDept(parseInt(deptId));
                      setShowAddModal(true);
                    }}
                    className="px-3 py-1 bg-white text-red-700 rounded text-sm font-medium hover:bg-red-100"
                  >
                    + સભ્ય ઉમેરો
                  </button>
                  <button
                    onClick={() => downloadDepartmentPDF(deptId, group.department_name, group.members)}
                    disabled={pdfLoading}
                    className="px-3 py-1 bg-white text-red-700 rounded text-sm font-medium hover:bg-red-100 disabled:opacity-50"
                  >
                    {pdfLoading ? '⏳' : '📄'} PDF
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-red-700">ક્રમ</th>
                      <th className="px-3 py-2 text-left text-red-700">નામ</th>
                      <th className="px-3 py-2 text-left text-red-700">હોદ્દો</th>
                      <th className="px-3 py-2 text-left text-red-700">મોબાઈલ</th>
                      <th className="px-3 py-2 text-left text-red-700">શહેર</th>
                      <th className="px-3 py-2 text-center text-red-700">કુર્તા</th>
                      <th className="px-3 py-2 text-center text-red-700">ID</th>
                      <th className="px-3 py-2 text-center text-red-700">ID આપેલ</th>
                      <th className="px-3 py-2 text-center text-red-700">કુર્તા આપેલ</th>
                      <th className="px-3 py-2 text-center text-red-700">ઉપલબ્ધ</th>
                      <th className="px-3 py-2 text-center text-red-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.members.map((member, index) => (
                      <tr key={member.id} className="border-b hover:bg-red-50">
                        <td className="px-3 py-2 text-red-600">{index + 1}</td>
                        <td className="px-3 py-2 font-medium text-red-700">{member.name}</td>
                        <td className="px-3 py-2 text-red-600">{member.position || "-"}</td>
                        <td className="px-3 py-2 text-red-600">{member.mobile || "-"}</td>
                        <td className="px-3 py-2 text-red-600">{member.city || "-"}</td>
                        <td className="px-3 py-2 text-center text-red-600">{member.kurta_size || "-"}</td>
                        <td className="px-3 py-2 text-center">
                          <StatusBadge value={member.id_card} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <ToggleBadge 
                            value={member.id_card_given} 
                            onChange={(v) => updateMemberOptimistic(member.id, { ...member, id_card_given: v })}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <ToggleBadge 
                            value={member.kurta_given} 
                            onChange={(v) => updateMemberOptimistic(member.id, { ...member, kurta_given: v })}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <AvailabilityBadge 
                            value={member.availability}
                            reason={member.availability_reason}
                            onChange={(v, r) => updateMemberOptimistic(member.id, { ...member, availability: v, availability_reason: r })}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => setEditingMember(member)}
                            className="text-blue-600 hover:text-blue-800 mr-2"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteMember(member.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          departments={departments}
          onClose={() => setEditingMember(null)}
          onSave={(data) => updateMember(editingMember.id, data)}
        />
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal
          departments={departments}
          defaultDept={addToDept}
          onClose={() => {
            setShowAddModal(false);
            setAddToDept(null);
          }}
          onSave={addMember}
        />
      )}

      {/* Add Department Modal */}
      {showAddDeptModal && (
        <AddDepartmentModal
          onClose={() => setShowAddDeptModal(false)}
          onSave={addDepartment}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportExcelModal
          members={members}
          departments={departments}
          selectedDept={selectedDept}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
    </HeaderLayout>
  );
}

// Status Badge Component
function StatusBadge({ value }) {
  if (value === 'Y') return <span className="text-green-600">✓</span>;
  if (value === 'N') return <span className="text-red-600">✗</span>;
  if (value === 'P') return <span className="text-yellow-600">⏳</span>;
  return <span className="text-gray-400">-</span>;
}

// Toggle Badge Component
function ToggleBadge({ value, onChange }) {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(value === 'Y' ? 'N' : 'Y');
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-8 h-8 rounded-full ${
        value === 'Y' 
          ? 'bg-green-100 text-green-600 hover:bg-green-200' 
          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
      }`}
    >
      {value === 'Y' ? '✓' : '✗'}
    </button>
  );
}

// Availability Badge Component
function AvailabilityBadge({ value, reason, onChange }) {
  const [showReasonInput, setShowReasonInput] = useState(false);
  const [reasonText, setReasonText] = useState(reason || '');

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (value === 'Y') {
      setShowReasonInput(true);
    } else {
      onChange('Y', '');
    }
  };

  const handleSaveReason = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('N', reasonText);
    setShowReasonInput(false);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowReasonInput(false);
  };

  if (showReasonInput) {
    return (
      <div className="flex flex-col gap-1 items-center">
        <input
          type="text"
          placeholder="કારણ"
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="w-24 px-2 py-1 text-xs border rounded"
          autoFocus
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={handleSaveReason}
            className="text-xs bg-red-600 text-white px-2 py-0.5 rounded"
          >
            સેવ
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs bg-gray-300 px-2 py-0.5 rounded"
          >
            ✗
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`px-2 py-1 rounded text-xs ${
        value === 'Y'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`}
      title={reason || ''}
    >
      {value === 'Y' ? '✓' : reason ? `✗ ${reason}` : '✗'}
    </button>
  );
}

// Export Excel Modal Component
function ExportExcelModal({ members, departments, selectedDept, onClose }) {
  const [exportType, setExportType] = useState('mobile');
  const [exportDept, setExportDept] = useState(selectedDept || 'all');
  const [downloading, setDownloading] = useState(false);

  const exportOptions = [
    { value: 'mobile', label: 'મોબાઈલ નંબર', icon: '📱', columns: ['ક્રમ', 'મોબાઈલ'] },
    { value: 'name', label: 'નામ', icon: '👤', columns: ['ક્રમ', 'નામ'] },
    { value: 'name_mobile', label: 'નામ + મોબાઈલ', icon: '👤📱', columns: ['ક્રમ', 'નામ', 'મોબાઈલ'] },
    { value: 'name_city', label: 'નામ + શહેર', icon: '👤🏙️', columns: ['ક્રમ', 'નામ', 'શહેર'] },
    { value: 'name_mobile_city', label: 'નામ + મોબાઈલ + શહેર', icon: '👤📱🏙️', columns: ['ક્રમ', 'નામ', 'મોબાઈલ', 'શહેર'] },
    { value: 'kurta_size', label: 'નામ + કુર્તા સાઈઝ', icon: '👕', columns: ['ક્રમ', 'નામ', 'કુર્તા સાઈઝ'] },
    { value: 'all_details', label: 'બધી વિગત', icon: '📋', columns: ['ક્રમ', 'નામ', 'હોદ્દો', 'મોબાઈલ', 'શહેર', 'કુર્તા સાઈઝ', 'ID Card', 'ID આપેલ', 'કુર્તા આપેલ', 'ઉપલબ્ધ', 'વિભાગ'] },
  ];

  const getExportData = () => {
    let dataToExport = members;
    
    // Filter by department if selected
    if (exportDept !== 'all') {
      dataToExport = members.filter(m => m.department_id === parseInt(exportDept));
    }

    let rows = [];
    
    switch (exportType) {
      case 'mobile':
        rows = dataToExport
          .filter(m => m.mobile)
          .map((m, i) => ({ 'ક્રમ': i + 1, 'મોબાઈલ': m.mobile }));
        break;
      
      case 'name':
        rows = dataToExport.map((m, i) => ({ 'ક્રમ': i + 1, 'નામ': m.name }));
        break;
      
      case 'name_mobile':
        rows = dataToExport.map((m, i) => ({ 
          'ક્રમ': i + 1, 
          'નામ': m.name, 
          'મોબાઈલ': m.mobile || '' 
        }));
        break;
      
      case 'name_city':
        rows = dataToExport.map((m, i) => ({ 
          'ક્રમ': i + 1, 
          'નામ': m.name, 
          'શહેર': m.city || '' 
        }));
        break;
      
      case 'name_mobile_city':
        rows = dataToExport.map((m, i) => ({ 
          'ક્રમ': i + 1, 
          'નામ': m.name, 
          'મોબાઈલ': m.mobile || '', 
          'શહેર': m.city || '' 
        }));
        break;
      
      case 'kurta_size':
        rows = dataToExport.map((m, i) => ({ 
          'ક્રમ': i + 1, 
          'નામ': m.name, 
          'કુર્તા સાઈઝ': m.kurta_size || '' 
        }));
        break;
      
      case 'all_details':
        rows = dataToExport.map((m, i) => ({
          'ક્રમ': i + 1,
          'નામ': m.name || '',
          'હોદ્દો': m.position || '',
          'મોબાઈલ': m.mobile || '',
          'શહેર': m.city || '',
          'કુર્તા સાઈઝ': m.kurta_size || '',
          'ID Card': m.id_card || '',
          'ID આપેલ': m.id_card_given === 'Y' ? 'હા' : 'ના',
          'કુર્તા આપેલ': m.kurta_given === 'Y' ? 'હા' : 'ના',
          'ઉપલબ્ધ': m.availability === 'Y' ? 'હા' : 'ના',
          'વિભાગ': m.department_name || ''
        }));
        break;
      
      default:
        rows = [];
    }
    
    return rows;
  };

  const downloadExcel = () => {
    setDownloading(true);
    
    try {
      const data = getExportData();
      
      if (data.length === 0) {
        alert('કોઈ ડેટા નથી!');
        setDownloading(false);
        return;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Set column widths
      const colWidths = Object.keys(data[0]).map(key => ({
        wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length)) + 2
      }));
      worksheet['!cols'] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Get sheet name
      const selectedDeptName = exportDept === 'all' 
        ? 'બધા_વિભાગ' 
        : departments.find(d => d.id === parseInt(exportDept))?.name?.replace(/\s+/g, '_') || 'Data';
      
      XLSX.utils.book_append_sheet(workbook, worksheet, selectedDeptName.substring(0, 31));

      // Generate filename
      const exportOption = exportOptions.find(o => o.value === exportType);
      const filename = `Patotsav_${exportOption?.label.replace(/\s+/g, '_')}_${selectedDeptName}.xlsx`;

      // Download
      XLSX.writeFile(workbook, filename);
      
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Export failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const selectedDeptName = exportDept === 'all' 
    ? 'બધા વિભાગ' 
    : departments.find(d => d.id === parseInt(exportDept))?.name || '';

  const dataCount = exportDept === 'all' 
    ? members.length 
    : members.filter(m => m.department_id === parseInt(exportDept)).length;

  const currentOption = exportOptions.find(o => o.value === exportType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-purple-700">📊 Export Excel</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>
        
        {/* Department Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-purple-700">વિભાગ પસંદ કરો</label>
          <select
            value={exportDept}
            onChange={(e) => setExportDept(e.target.value)}
            className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="all">બધા વિભાગ ({members.length})</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>
                [{d.id}] {d.name} ({d.member_count})
              </option>
            ))}
          </select>
        </div>

        {/* Export Type Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-purple-700">શું Export કરવું છે?</label>
          <div className="grid grid-cols-1 gap-2">
            {exportOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setExportType(opt.value)}
                className={`p-3 rounded-lg border text-left flex items-center gap-3 transition-all ${
                  exportType === opt.value
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                }`}
              >
                <span className="text-xl">{opt.icon}</span>
                <div>
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-gray-500">Columns: {opt.columns.join(', ')}</div>
                </div>
                {exportType === opt.value && (
                  <span className="ml-auto text-purple-600">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Info Badge */}
        <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <span className="text-purple-700 font-medium">
              📊 {selectedDeptName}
            </span>
            <span className="text-purple-600 bg-purple-100 px-2 py-1 rounded text-sm">
              {dataCount} સભ્યો
            </span>
          </div>
          <div className="text-xs text-purple-600 mt-1">
            Export: {currentOption?.label} ({currentOption?.columns.length} columns)
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={downloadExcel}
            disabled={downloading || dataCount === 0}
            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <>
                <span className="animate-spin">⏳</span> Downloading...
              </>
            ) : (
              <>
                <span>📥</span> Download Excel (.xlsx)
              </>
            )}
          </button>
          
          <button
            onClick={onClose}
            className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            બંધ કરો
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Member Modal
function EditMemberModal({ member, departments, onClose, onSave }) {
  const [form, setForm] = useState({ ...member });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-red-700">સભ્ય એડિટ કરો</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-red-700">નામ</label>
            <input
              type="text"
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-red-300 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-red-700">હોદ્દો</label>
            <input
              type="text"
              value={form.position || ""}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              className="w-full px-3 py-2 border border-red-300 rounded"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">મોબાઈલ</label>
              <input
                type="text"
                value={form.mobile || ""}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">શહેર</label>
              <input
                type="text"
                value={form.city || ""}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">કુર્તા સાઈઝ</label>
              <select
                value={form.kurta_size || ""}
                onChange={(e) => setForm({ ...form, kurta_size: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              >
                <option value="">Select</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">ID Card</label>
              <select
                value={form.id_card || ""}
                onChange={(e) => setForm({ ...form, id_card: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              >
                <option value="">Select</option>
                <option value="Y">Yes (Y)</option>
                <option value="N">No (N)</option>
                <option value="P">Pending (P)</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">ID આપેલ</label>
              <select
                value={form.id_card_given || "N"}
                onChange={(e) => setForm({ ...form, id_card_given: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              >
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">કુર્તા આપેલ</label>
              <select
                value={form.kurta_given || "N"}
                onChange={(e) => setForm({ ...form, kurta_given: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              >
                <option value="N">No</option>
                <option value="Y">Yes</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-red-700">ઉપલબ્ધતા</label>
            <div className="flex gap-4">
              <select
                value={form.availability || "Y"}
                onChange={(e) => setForm({ ...form, availability: e.target.value })}
                className="px-3 py-2 border border-red-300 rounded"
              >
                <option value="Y">ઉપલબ્ધ</option>
                <option value="N">અનુપલબ્ધ</option>
              </select>
              {form.availability === 'N' && (
                <input
                  type="text"
                  placeholder="કારણ"
                  value={form.availability_reason || ""}
                  onChange={(e) => setForm({ ...form, availability_reason: e.target.value })}
                  className="flex-1 px-3 py-2 border border-red-300 rounded"
                />
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-red-300 rounded hover:bg-red-50 text-red-700"
          >
            રદ કરો
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            સેવ કરો
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Member Modal
function AddMemberModal({ departments, defaultDept, onClose, onSave }) {
  const [form, setForm] = useState({
    department_id: defaultDept || departments[0]?.id || 1,
    name: "",
    position: "",
    city: "",
    mobile: "",
    kurta_size: "",
    id_card: "N",
    id_card_given: "N",
    kurta_given: "N",
    availability: "Y",
    availability_reason: ""
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-red-700">નવો સભ્ય ઉમેરો</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-red-700">વિભાગ</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-red-300 rounded"
            >
              {departments.map(d => (
                <option key={d.id} value={d.id}>
                  [{d.id}] {d.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-red-700">નામ *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-red-300 rounded"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-red-700">હોદ્દો</label>
            <input
              type="text"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              className="w-full px-3 py-2 border border-red-300 rounded"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">મોબાઈલ</label>
              <input
                type="text"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">શહેર</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">કુર્તા સાઈઝ</label>
              <select
                value={form.kurta_size}
                onChange={(e) => setForm({ ...form, kurta_size: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              >
                <option value="">Select</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-red-700">ID Card</label>
              <select
                value={form.id_card}
                onChange={(e) => setForm({ ...form, id_card: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 rounded"
              >
                <option value="N">No (N)</option>
                <option value="Y">Yes (Y)</option>
                <option value="P">Pending (P)</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-red-300 rounded hover:bg-red-50 text-red-700"
          >
            રદ કરો
          </button>
          <button
            onClick={() => form.name && onSave(form)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            ઉમેરો
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Department Modal
function AddDepartmentModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    id: "",
    name: ""
  });
  const [error, setError] = useState("");

  const handleSave = () => {
    if (!form.id || !form.name) {
      setError("વિભાગ ID અને નામ જરૂરી છે");
      return;
    }
    if (isNaN(form.id) || parseInt(form.id) <= 0) {
      setError("વિભાગ ID એક positive number હોવો જોઈએ");
      return;
    }
    onSave({
      id: parseInt(form.id),
      name: form.name.trim()
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4 text-green-700">નવો વિભાગ ઉમેરો</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-green-700">વિભાગ ID (નંબર) *</label>
            <input
              type="number"
              value={form.id}
              onChange={(e) => {
                setForm({ ...form, id: e.target.value });
                setError("");
              }}
              className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="દા.ત. 43"
              min="1"
            />
            <p className="text-xs text-gray-500 mt-1">આ ID unique હોવો જોઈએ</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1 text-green-700">વિભાગ નામ *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setError("");
              }}
              className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="દા.ત. ભોજન વ્યવસ્થા"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-green-300 rounded hover:bg-green-50 text-green-700"
          >
            રદ કરો
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            ઉમેરો
          </button>
        </div>
      </div>
    </div>
  );
}